import { Column, ColumnKeys, Filter, getModulesValues, getObjectTypeModuleValues, getObjectTypeValues, GlobalExclusionsEvents } from '../types';
import { translateFn } from '../../../utils/translator.helper';
import { ColumnDetails, ColumnType, GridEvents } from '../../../grid/types/common';
import { GridColumnRenderers, GridViewMetadata, ViewConfiguration, ViewType } from '../../../grid/gridView/types';
import { Rights } from '@nirvana/shared/constants';
import { SelectOption } from '../../../ui/gzSelect/types';
import { GridBaseDataProvider } from '../../../grid/GridBaseDataProvider';
import { GridViewWebComponent } from '../../../grid/gridView';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { EnumListItem } from '../../../ui/gzEnumList/types';
import { getLabelForEnumValue } from '../../../grid/utils/columns';
import { dataGridStrings } from '../../../grid/utils/gridStrings';
import { html } from 'lit';
import { dateFormatter } from '@nirvana/shared/time';
import { htmlDecode } from '../../../utils/escape.utils';
import { RequiredValidator } from '../../../ui/gzForm/validators/required.validator';
import { ExclusionItemWildcardValidator, ExclusionValidator } from '../../../ui/gzForm/validators/exclusion.validator';
import { GridCellEditWebComponent } from '../../../grid/uiElements/gridCellEdit';
import { GzFormFieldWebComponent } from '../../../ui/gzForm/formField';
import { getAllAvailableModulesByObjectType } from '../../../utils/globalExclusions/exclusionsValidationsUtils';

export class GlobalExclusionsGridConfig {

  private gridColumns: Column[] = [
    Column.OBJECT_TYPE,
    Column.EXCLUDED_ITEMS,
    Column.MODULES,
    Column.REMARKS,
    Column.IN_LISTS,
    Column.ADDED_ON,
  ];

  private predefinedFiltersKeys: Array<ColumnKeys> = [
    ColumnKeys.OBJECT_TYPE,
    ColumnKeys.EXCLUDED_ITEMS,
    ColumnKeys.MODULES,
  ];

  private gridDefaultFilters: Array<Filter> = [{objectType: {in: []}}, {excludedItems: {contains: ''}}, {modules: {in: []}}];

  private allColumnDetails: Array<ColumnDetails<Column, ColumnKeys>> = [
    {
      column: Column.OBJECT_TYPE,
      key: ColumnKeys.OBJECT_TYPE,
      columnName: translateFn('Object type'),
      type: ColumnType.ENUM,
      values: getObjectTypeValues(),
      removable: true,
      editingDisabled: true,
    },
    {
      column: Column.EXCLUDED_ITEMS,
      key: ColumnKeys.EXCLUDED_ITEMS,
      columnName: translateFn('Excluded items'),
      type: ColumnType.STRING,
      removable: false,
      sortable: true,
    },
    {
      column: Column.MODULES,
      key: ColumnKeys.MODULES,
      columnName: translateFn('Modules'),
      type: ColumnType.ENUM,
      values: getModulesValues(),
      removable: true,
    },
    {
      column: Column.REMARKS,
      key: ColumnKeys.REMARKS,
      columnName: translateFn('Remarks'),
      type: ColumnType.STRING,
      removable: true,
      sortable: true,
    },
    {
      column: Column.IN_LISTS,
      key: ColumnKeys.IN_LISTS,
      columnName: translateFn('In lists'),
      type: ColumnType.STRING,
      removable: true,
      sortable: true,
      filteringDisabled: true,
      editingDisabled: true,
    },
    {
      column: Column.ADDED_ON,
      key: ColumnKeys.ADDED_ON,
      columnName: translateFn('Added on'),
      type: ColumnType.STRING,
      removable: true,
      sortable: true,
      filteringDisabled: true,
      editingDisabled: true,
    },
  ];

  private defaultViewProps: ViewConfiguration<Column, ColumnKeys> = {
    compactView: false,
    displayedColumns: this.gridColumns,
    filters: this.gridDefaultFilters,
    name: '',
    pageSize: 50,
    type: ViewType.DEFAULT,
    id: 'all-exclusions-grid',
  };

  private columnRenderers: GridColumnRenderers = {
    [ColumnKeys.IN_LISTS]: this.listCountRenderer,
    [ColumnKeys.IN_POLICIES]: this.policiesCountRenderer,
    [ColumnKeys.MODULES]: this.modulesRenderer.bind(this),
    [ColumnKeys.ADDED_ON]: this.addedOnRenderer.bind(this),
  };

  private pageSizeOptions: Array<SelectOption> = [
    {
      title: '10',
      value: '10',
    },
    {
      title: '50',
      value: '50',
    },
    {
      title: '100',
      value: '100',
    },
  ];

  private inlineRowActions: Array<SelectOption> = [
    {
      title: translateFn('Edit list assignment'),
      value: GlobalExclusionsEvents.ASSIGN_EXCLUSION_ITEM,
      disabled: !Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE]),
    },
    {
      title: translateFn('Edit exclusion'),
      value: GridEvents.EDIT_ROW,
      disabled: !Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE]),
    },
    {
      title: translateFn('Delete'),
      value: GlobalExclusionsEvents.DELETE_EXCLUSION_ITEM,
      customClass: 'danger',
      disabled: !Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE]),
    },
  ];

  constructor(private readonly dataProvider: GridBaseDataProvider<Column, ColumnKeys>, private $gridView: GridViewWebComponent<Column, ColumnKeys>) {
  }

  public setGridViewMetadata(grid: GridViewWebComponent<Column, ColumnKeys>) {
    if (Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE])) {
      grid.inlineRowActions = this.inlineRowActions;
    }

    grid.selectionStoreConfig = {uniqueIdentifier: 'id'};
    Object.assign(grid, this.getGridViewMetadata());
  }

  public getGridViewMetadata(): GridViewMetadata<Column, ColumnKeys> {
    return {
      viewId: 'all-exclusions-grid',
      filters: this.gridDefaultFilters,
      predefinedFiltersKeys: this.predefinedFiltersKeys,
      columns: this.gridColumns,
      pageSizeOptions: this.pageSizeOptions,
      columnRenderers: this.columnRenderers,
      allColumnDetails: this.allColumnDetails,
      dataProvider: this.dataProvider,
      defaultViewProps: this.defaultViewProps,
      pageSize: 50,
      pageNumber: 1,
      sortDirection: 'desc',
      columnConfigs: [],
      isCompactViewChecked: false,
      hasSelectableRows: true,
      hasPagination: true,
      showGlobalActions: true,
      showConfigurableArea: false,
      enableCSVExport: true,
    };
  }

  private listCountRenderer(rowData, columnDetails: ColumnDetails<Column, ColumnKeys>) {
    const cellValue = rowData.item[columnDetails.key];

    if (isEmpty(cellValue)) {
      return '-';
    }

    return html`<span class="cell-label">${cellValue.length} lists</span>`;
  }

  private policiesCountRenderer(rowData, columnDetails: ColumnDetails<Column, ColumnKeys>) {
    const cellValue = rowData.item[columnDetails.key];

    if (isEmpty(cellValue)) {
      return '-';
    }

    return html`<span class="text-semibold accent cell-label">${cellValue.length} policies</span>`;
  }

  private modulesRenderer(rowData, columnDetails: ColumnDetails<Column, ColumnKeys>) {
    const activeItem: any = this.$gridView.controller?.view.getEditedGridItem();
    const isActive = activeItem && activeItem.index === rowData.index;
    let cellValues, objectType;

    if (isActive) {
      cellValues = activeItem.item[columnDetails.key];
      objectType = activeItem.item.objectType;
    } else {
      const availableModules = getAllAvailableModulesByObjectType(rowData.item.objectType);
      cellValues = rowData.item[columnDetails.key].filter((value: number) => availableModules.includes(value));
      objectType = rowData.item.objectType;
    }

    const modulesColumnValues = columnDetails.values;

    const dropdownOptions = modulesColumnValues && modulesColumnValues.map(module => {
      // match cell values against all the module values so we can get the selected items for the dropdown
      const matches = cellValues.filter(value => value === module.value);
      const selected = !!matches.length;

      let disabled = false;
      const moduleConfigurations = getObjectTypeModuleValues(objectType);

      for (const moduleConfig of moduleConfigurations) {
        if (moduleConfig.value === module.value) {
          disabled = !!moduleConfig.disabled;
          break;
        }
      }

      const entry: EnumListItem = {
        label: module.label,
        value: module.value,
        parent: module.parent,
        selected: selected,
        disabled: disabled,
      };

      return entry;
    });

    const labels = cellValues.map(val => getLabelForEnumValue(columnDetails, val)).join(', ');
    const { confirmString, allItemsLabel } = dataGridStrings().enumColumnStrings;

    return html`
      <gz-grid-cell-edit
        class="cell-label"
        .type="${columnDetails.type}"
        .value="${labels}"
        id="${columnDetails.key}"
        .filterOptions="${dropdownOptions}"
        allItemsLabel="${allItemsLabel}"
        confirmActionButtonString="${confirmString}"
        @selectionChange="${this.modulesSelectionChangeHandler.bind(this, columnDetails.key, rowData)}"></gz-grid-cell-edit>`;
  }

  private modulesSelectionChangeHandler(key: string, rowData, e: CustomEvent) {
    this.$gridView.controller?.view.updateKeyInEditedGridItemData(key, e.detail.selectedValues);

    const gridCellModule = e.target as GridCellEditWebComponent;
    const selectedModules = (gridCellModule.shadowRoot?.querySelector("gz-form-field") as GzFormFieldWebComponent).Input.getFormValue();
    const gridCellPath = (gridCellModule?.parentElement?.parentElement?.previousSibling as HTMLElement).querySelector("gz-grid-cell-edit");
    const selectedPath = (gridCellPath?.shadowRoot?.querySelector("gz-form-field") as GzFormFieldWebComponent).Input.getFormValue();

    this.$gridView.controller?.setValidatorsOnRowAndColumn(rowData.index, ColumnKeys.EXCLUDED_ITEMS, [
        RequiredValidator(''),
        ExclusionValidator(rowData.item.objectType, ''),
        ExclusionItemWildcardValidator({
        type: rowData.item.objectType,
        path: selectedPath,
        modules: selectedModules,
      }, translateFn('You cannot use wildcards')),
    ]);
  }

  private addedOnRenderer(rowData, columnDetails: ColumnDetails<Column, ColumnKeys>) {
    const cellValue = rowData.item[columnDetails.key];

    if (isEmpty(cellValue)) {
      return '-';
    }

    const formattedDate = dateFormatter(cellValue, Application.Main.getUser().profile.timezone);

    return html`<span class="cell-label label">${htmlDecode(formattedDate)}</span>`;
  }

}
